import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { HiArrowTopRightOnSquare } from 'react-icons/hi2'
import { BsArrowRight } from 'react-icons/bs'
import AuthenticatedRoutes from '../../Utils/AuthenticatedRoutes'
import { ask } from '../../Api/search'
import { updateStatus } from '../../Api/dashboard'
import { fetchComponentData } from '../../Api/transcriptAndSummary'
import './functionDashboard.css'
import NavbarOpen from '../NavLinks/NavbarOpen'
import { DateTime } from 'luxon'
import { useContext } from 'react'
import { Platform } from '../../Context/PlatformContext'
import Loader from '../Loader/Loader'
import { useRef } from 'react'
import { Box, Button, Flex, Input, Text } from '@chakra-ui/react'
import {
  firstMeetingData,
  onboardingActionItems,
  OnboardingtemplatisedNotes,
  taggedMeetingData,
} from '../../Utils/data'
import { useSelector } from 'react-redux'
import { NavbarContext } from '../../Context/NavbarContext'
import { addPrompt } from '../../Api/meetings'
import { GetStartedContext } from '../../Context/GetStartedContext'

const timeFilterTypes = ['yesterday', 'last-week', 'last-month']

export const removePrefix = (name) => {
  if (name.startsWith('function_')) {
    return name.substring(9).replace('-', ' ')
  }
  if (name.startsWith('event_')) {
    return name.substring(6).replace('-', ' ')
  }
  if (name.startsWith('tag_')) {
    return name.substring(4).split('___')[1].replace('-', ' ')
  }
  if (name.startsWith('rec_')) {
    return name.substring(4).split('___')[1].replace('-', ' ')
  }
}

const getPrefix = (name) => {
  if (name.startsWith('function_')) {
    return ['function', name.substring(9)]
  }
  if (name.startsWith('event_')) {
    return ['event', name.substring(6)]
  }
  if (name.startsWith('tag_')) {
    return ['tag', name.substring(4).split('___')[0]]
  }
  if (name.startsWith('rec_')) {
    return ['recurrent', name.substring(4).split('___')[0]]
  }
}

const FunctionalDashboard = () => {
  const navigate = useNavigate()
  const user = useSelector((state) => state?.user?.data)
  const [searchParams] = useSearchParams()
  const [name, setName] = useState('')

  const platform = useContext(Platform)
  const {setSearchIsOpen} = useContext(NavbarContext)

  const now = DateTime.now()

  // creating date time objects for last week, yesterday and last month
  const yesterday = now.minus({ days: 1 })
  const lastWeek = now.minus({ days: 7 })
  const lastMonth = now.minus({ days: 28 })

  // formatting the date time objects to display in the UI
  const lastWeekDay = lastWeek.toFormat('LLL-dd')
  const yestMonth = yesterday.toFormat('LLL-dd')
  const currentDay = now.toFormat('LLL-dd')
  const lastStartDate = lastMonth.toFormat('LLL dd')
  const currentDate = now.toFormat('LLL dd')
  const [dummyFirstMeetingData, setDummyFirstMeetingData] = useState()
  const [dummyActionItems, setDummyActionItems] = useState([])
  const [dummyTemplatisedNotes, setDummyTemplatisedNotes] = useState([])
  const [dummyTaggedSummary, setDummyTaggedSummary] = useState([])
  useEffect(() => {
    setName(removePrefix(searchParams.get('name')))
    if (
      searchParams.get('name').includes('tag') &&
      searchParams.get('name').includes('onboarding')
    ) {
      let firstName = user?.name?.split(' ')[0]
      setDummyFirstMeetingData(
        firstMeetingData(
          firstName?.charAt(0).toUpperCase() + firstName?.slice(1)
        )
      )
      setDummyActionItems(onboardingActionItems)
      setDummyTemplatisedNotes(OnboardingtemplatisedNotes(user?.id))
      setDummyTaggedSummary(taggedMeetingData.event.summary)
    }
  }, [searchParams, user])

  const [timeFilter, setTimeFilter] = useState('last-week')

  const [showSummary, setShowSummary] = useState([])

  const [overallSummary, setOverallSummary] = useState('')
  const [summary, setSummary] = useState('')
  const [answer, setAnswer] = useState('')

  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)

  const [actionItems, setActionItems] = useState([])
  const [templatisedNotes, setTemplatisedNotes] = useState([])
  const [summaries, setSummaries] = useState([])

  const [hoverItem, setHoverItem] = useState('')

  const [newPrompt, setNewPrompt] = useState('')
  const [newPromptLoading, setNewPromptLoading] = useState(false)

  const searchInputRef = useRef()
  const {currentStep} = useContext(GetStartedContext)

  const showSummaryHandler = useCallback(
    (index) => {
      const temp = [...showSummary]
      const indexFlag = temp.indexOf(index)
      if (indexFlag === -1) {
        temp.push(index)
      } else {
        temp.splice(indexFlag, 1)
      }
      setShowSummary([...temp])
    },
    [showSummary]
  )

  const fetchActionItemsComponentData = useCallback(async () => {
    const apiResponse = await fetchComponentData(
      'actionItems',
      [searchParams.get('name')],
      undefined,
      timeFilter
    )
    // console.log('apiResponse', apiResponse)
    if (apiResponse?.length > 0) {
      setActionItems(apiResponse)
    } else {
      setActionItems([])
    }
  }, [timeFilter, searchParams])

  const fetchTemplatisedNotesComponentData = useCallback(async () => {
    const apiResponse = await fetchComponentData(
      'templatisedNotes',
      // ['event_55mi67ip9a9rr4sqi788vm5a2j', searchParams.get('name')],
      [searchParams.get('name')],
      undefined,
      timeFilter
    )
    // console.log('apiResponse', apiResponse)
    if (apiResponse) {
      setTemplatisedNotes(apiResponse)
    }
  }, [timeFilter, searchParams])

  const fetchSummaryComponentData = useCallback(async () => {
    const apiResponse = await fetchComponentData(
      'eventSummary',
      [searchParams.get('name')],
      undefined,
      timeFilter
    )
    if (apiResponse?.length > 0) {
      setSummaries([
        ...apiResponse.map((item) => ({
          ...item,
          date: ` ${DateTime.fromMillis(Number(item.start_time)).toFormat(
            'LLLL-dd'
          )}`,
        })),
      ])
    } else {
      setSummaries([])
    }
  }, [timeFilter, searchParams])

  const fetchOverallSummaryComponentData = useCallback(async () => {
    const apiResponse = await fetchComponentData(
      'overallSummary',
      [searchParams.get('name')],
      undefined,
      undefined
    )
    // console.log(apiResponse)
    if (apiResponse) {
      setOverallSummary(apiResponse)
    } else {
      setOverallSummary('')
    }
  }, [searchParams])

  const handleAddPrompt = async (prompt) => {
    setNewPromptLoading(true)
    
    const [eventType, eventId] = getPrefix(searchParams.get('name'))

    await addPrompt(eventId, prompt, eventType)
    setNewPrompt('')
    setNewPromptLoading(false)
  }

  const submitHandler = useCallback(
    async (event) => {
      event.preventDefault()
      setIsSearchLoading(true)
      try {
        if (search.trim()) {
          const apiResponse = await ask(search.trim())
          setIsSearchLoading(false)
          if (apiResponse?.answer[0]?.answer) {
            setAnswer(apiResponse?.answer[0]?.answer)
          } else {
            setAnswer('')
          }
        }
      } catch (e) {
        // console.log(e)
        setIsSearchLoading(false)
      }
    },
    [search]
  )

  const updateActionItemStatusHandler = useCallback(
    async (id, status) => {
      const apiResponse = await updateStatus(id, status)
      if (apiResponse) {
        fetchActionItemsComponentData()
      }
    },
    [fetchActionItemsComponentData]
  )

  useEffect(() => {
    fetchSummaryComponentData()
    fetchActionItemsComponentData()
    fetchOverallSummaryComponentData()
    fetchTemplatisedNotesComponentData()
  }, [
    fetchSummaryComponentData,
    fetchActionItemsComponentData,
    fetchOverallSummaryComponentData,
    fetchTemplatisedNotesComponentData,
  ])

  const updateFirstActionItemStatusHandler = (id, newStatus) => {
    const itemIndex = dummyActionItems.findIndex((item) => item.id === id)
    if (itemIndex === -1) {
      // Action item not found
      return
    }
    // Create a new action item object with the updated status
    const updatedItem = {
      ...dummyActionItems[itemIndex],
      status: newStatus,
    }
    // Create a new array with the updated action item
    const updatedItems = [
      ...dummyActionItems.slice(0, itemIndex),
      updatedItem,
      ...dummyActionItems.slice(itemIndex + 1),
    ]
    // Update the state with the new array of action items
    setDummyActionItems(updatedItems)
  }

  const dummyFirstMeetSummary =
    dummyFirstMeetingData?.event?.summary[0]?.summary

  const handleShowSearchBar = () => {
    setShowSearch(true)
    searchInputRef.current.focus()
    setSearch('')
    setAnswer('')
  }
  const hasYesterdaySummary = useMemo(() => {
    const yestISODate = yesterday.toISODate()
    return summaries?.some((summary) => summary.date.startsWith(yestISODate))
  })
  return (
    <div className='container container_light dashboard'>
      <NavbarOpen />
      <AuthenticatedRoutes auth={true} />

      <div
        className={showSearch ? 'search-container active' : 'search-container'}
      >
        <div
          onClick={() => {
            setShowSearch(false)
            setSearch('')
            setAnswer('')
          }}
          style={{ height: '100%', width: '100%', position: 'absolute' }}
        />
        <div style={{ zIndex: 1 }} className='search-wrapper'>
          <div className='question-box'>
            <form onSubmit={submitHandler}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                ref={searchInputRef}
                placeholder='Enter question to Ask Arrowhead about meetings...'
              />
            </form>
            <div className='actions'>
              <h4>
                Searching within <span>{name}</span>
              </h4>
              {/* <button>See previous questions</button> */}
            </div>
          </div>
          {isSearchLoading && <Loader />}
          {!isSearchLoading && answer && (
            <div className='answer-box'>
              <p>{answer}</p>
              <button>
                Ask another question{' '}
                {platform === 'Mac' ? (
                  <>
                    <span>⌘</span>
                    <span>K</span>
                  </>
                ) : (
                  <>
                    <span>Ctrl</span>
                    <span>G</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      <h1>{name}</h1>
      <div style={{ position: 'relative', display: 'flex' }}>
        <div className='tabs'>
          <button
            className={timeFilter === timeFilterTypes[0] ? 'active' : ''}
            onClick={() => setTimeFilter(timeFilterTypes[0])}
            disabled={
              summaries.length === 0 && timeFilter !== timeFilterTypes[0]
            }
            style={
              summaries.length === 0 && timeFilter !== timeFilterTypes[0]
                ? { opacity: 0.5, cursor: 'not-allowed' }
                : {}
            }
          >
            Yesterday
            <br />
            {yestMonth}
          </button>
          <button
            className={timeFilter === timeFilterTypes[1] ? 'active' : ''}
            onClick={() => setTimeFilter(timeFilterTypes[1])}
          >
            Last 7 days
            <br />
            {lastWeekDay + ' - ' + currentDay}
          </button>
          <button
            className={timeFilter === timeFilterTypes[2] ? 'active' : ''}
            onClick={() => setTimeFilter(timeFilterTypes[2])}
          >
            Last 28 Days
            <br />
            {lastStartDate + ' - ' + currentDate}
          </button>
        </div>
        <div
          className='askbar'
          style={{ cursor: 'pointer' }}
          onClick={() => setSearchIsOpen(true)} 
        >
          <div className='tag'>Ask Arrowhead</div>
          {platform === 'Mac' ? (
            <>
              <div className='key'>⌘</div>
              <div className='key'>K</div>
            </>
          ) : (
            <>
              <div className='key'>Ctrl</div>
              <div className='key'>G</div>
            </>
          )}
        </div>
      </div>
      <ul className='cards'>
        <li>
          {/* {overallSummary && (
            <div className='card-box'>
              <span>Overall summary (coming soon)</span>
              <div className="card">
                <p>{summary}</p>
              </div>
            </div>
          )} */}
          <div className='card-box'>
            <span>Overall action items</span>
            <div className='card'>
              {searchParams.get('name').includes('tag') &&
              searchParams.get('name').includes('onboarding') ? (
                  <ul>
                    {dummyActionItems.map((item) => (
                      <li
                        key={item.id}
                        className={item.status === 1 ? 'active' : ''}
                      >
                        <div
                          style={{color: '#222222', fontSize: '16px'}}
                          onClick={
                            item?.status === 1
                              ? () =>
                                updateFirstActionItemStatusHandler(item.id, 0)
                              : () =>
                                updateFirstActionItemStatusHandler(item.id, 1)
                          }
                        >
                          {item.status === 1 ? '✔' : ''}
                        </div>
                        <p style={{color: '#222222', fontSize: '16px'}}>
                          <strong style={{color: '#222222', fontSize: '16px'}}>{item.user}</strong>: to {item.item}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : actionItems?.length > 0 ? (
                  <ul>
                    {actionItems.map((item) => (
                      <li
                        key={item.id}
                        className={item.status === 1 ? 'active' : ''}
                      >
                        <div
                          style={{color: '#222222', fontSize: '16px'}}
                          onClick={
                            item?.status === 1
                              ? () => updateActionItemStatusHandler(item.id, 0)
                              : () => updateActionItemStatusHandler(item.id, 1)
                          }
                        >
                          {item.status === 1 ? '✔' : ''}
                        </div>
                        <p style={{color: '#222222', fontSize: '16px'}}>
                          <strong style={{color: '#222222', fontSize: '16px'}}>{item.user}</strong>: to {item.item}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className='dashboard-placeholder'>
                  No action item found!
                  </span>
                )}
            </div>
          </div>

          {searchParams.get('name').includes('tag') &&
          searchParams.get('name').includes('onboarding') ? (
              dummyTemplatisedNotes.map((note, index) => {
                return Object.keys(note).map((question, questionIndex) => (
                  <div
                    className='card-box'
                    key={`answer-${index}-${questionIndex}`}
                  >
                    <span>{question}</span>
                    <div className='card'>
                      <ul style={{gap: 0}}>
                        {note[question].map(
                          (
                            { answer, title, start_time, event_id },
                            answerIndex
                          ) => (
                            <>
                              <Button
                                border='1px solid rgba(0, 0, 0, 0.4)'
                                bg='white'
                                _hover={{
                                  bg: '#F3F3F3',
                                  border: '1px solid #FFFFFF',
                                }}
                                mb='8px'
                                borderRadius='48px'
                                padding='6px 8px'
                                minH={0}
                                h='21px'
                                fontSize='12px'
                                lineHeight='24px'
                                colorScheme='brandWhite'
                                color='black'
                                onClick={() =>
                                  navigate(
                                    `/transcripts-summaries/summary?eventId=${event_id}&tab=HIGH&eventName=${title}`
                                  )
                                }
                                fontFamily='Raleway'
                                w='fit-content'
                              >
                                {title}
                              &nbsp;
                                <span style={{ opacity: 0.6 }}>
                                  {DateTime.fromMillis(
                                    parseInt(summaries[0]?.start_time)
                                  ).toFormat('dd MMM')}
                                </span>
                              </Button>

                              {answer.split('-').filter(point => point && point.trim() !== '').map((point, pointIndex) => (
                                <Text
                                  onMouseLeave={() => setHoverItem('')}
                                  mb='0'
                                  color='blackAlpha.600'
                                  key={`answer-${index}-${questionIndex}-${answerIndex}-${pointIndex}`}
                                  lineHeight='24px'
                                >
                              • {point.trim()}
                              &nbsp;
                                </Text>
                              ))}

                              <Box mb='42px' />
                            
                            </>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                ))
              })
            ) : // summaries?.length > 0 &&
            templatisedNotes && templatisedNotes.length ? (
              templatisedNotes.map((note, index) => {
                return Object.keys(note).map((question, questionIndex) => (
                  <div
                    className='card-box'
                    key={`answer-${index}-${questionIndex}`}
                  >
                    <span>{question}</span>
                    <div className='card' style={{ maxHeight: 'none' }}>
                      <ul style={{gap: 0}}>
                        {note[question].map(
                          (
                            { answer, title, start_time, event_id },
                            answerIndex
                          ) => (
                            <>
                            
                              <Button
                              // display={
                              //   hoverItem ===
                              //   `${index}-${questionIndex}-${answerIndex}`
                              //     ? 'inline-flex'
                              //     : 'none'
                              // }
                                display='inline-flex'
                                border='1px solid rgba(0, 0, 0, 0.4)'
                                bg='white'
                                _hover={{
                                  bg: '#F3F3F3',
                                  border: '1px solid #FFFFFF',
                                }}
                                borderRadius='48px'
                                padding='6px 8px'
                                minH={0}
                                h='21px'
                                fontSize='12px'
                                lineHeight='24px'
                                colorScheme='brandWhite'
                                color='black'
                                onClick={() =>
                                  navigate(
                                    `/transcripts-summaries/summary?eventId=${event_id}&tab=HIGH&eventName=${title}`
                                  )
                                }
                                fontFamily='Raleway'
                                mb='8px'
                                w='fit-content'
                              >
                                {title}
                              &nbsp;
                                <span style={{ opacity: 0.6 }}>
                                  {DateTime.fromMillis(
                                    parseInt(start_time)
                                  ).toFormat('dd MMM')}
                                </span>
                              </Button>
                            
                              {answer.split('-').filter(point => point && point.trim() !== '').map((point, pointIndex) => (
                                <Text
                                  onMouseEnter={() =>
                                    setHoverItem(
                                      `${index}-${questionIndex}-${answerIndex}`
                                    )
                                  }
                                  onMouseLeave={() => setHoverItem('')}
                                  mb='0'
                                  color='black'
                                  key={`answer-${index}-${questionIndex}-${answerIndex}-${pointIndex}`}
                                  lineHeight='24px'
                                  fontFamily='Raleway'
                                >
                              • {point.trim()}
                              &nbsp;
                                </Text>
                              ))}

                              <Box mb='42px' />
                            </>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                ))
              })
            ) : (
              <></>
            )}
        </li>
        <li>
          <div className='card-box'>
            <span>Individual summary</span>
            <div className='card'>
              {summaries?.length > 0 ? (
                searchParams.get('name').includes('tag') &&
                searchParams.get('name').includes('onboarding') ? (
                    <ul>
                      {summaries.map((summary, index) => (
                        <li
                          key={index}
                          className={
                            ((dummyFirstMeetSummary && index === 0) ||
                            (dummyTaggedSummary && index === 1)) &&
                          showSummary.indexOf(index) !== -1
                              ? 'active'
                              : ''
                          }
                        >
                          <div className='header'>
                            <button
                              onClick={
                                (dummyFirstMeetSummary && index === 0) ||
                              (dummyTaggedSummary && index === 1)
                                  ? () => showSummaryHandler(index)
                                  : null
                              }
                              className={
                                (dummyFirstMeetSummary && index === 0) ||
                              (dummyTaggedSummary && index === 1)
                                  ? 'active'
                                  : ''
                              }
                            >
                              <BsArrowRight size={16} />
                              <strong>{summary.title}</strong>
                              {summary.date}
                            </button>
                            {((dummyFirstMeetSummary && index === 0) ||
                            (dummyTaggedSummary && index === 1)) && (
                              <Link
                                to={`/transcripts-summaries/summary?eventId=${summary.event_id}&tab=HIGH&eventName=${'Start with Arrowhead! 🏹🚀'}`}
                              >
                                <HiArrowTopRightOnSquare size={16} />
                              </Link>
                            )}
                          </div>
                          {dummyFirstMeetSummary && index === 0 ? (
                            <p>{dummyFirstMeetSummary}</p>
                          ) : (
                            <p>{dummyTaggedSummary[0].summary}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul>
                      {summaries.map((summary, index) => (
                        <li
                          key={`${summary.id}-${index}`}
                          className={
                            summary?.summary && showSummary.indexOf(index) !== -1
                              ? 'active'
                              : ''
                          }
                        >
                          <div className='header'>
                            <button
                              onClick={
                                summary?.summary
                                  ? () => showSummaryHandler(index)
                                  : null
                              }
                              className={summary?.summary ? 'active' : ''}
                            >
                              <BsArrowRight size={16} />
                              <strong>{summary.title}</strong>
                              {summary.date}
                            </button>
                            {summary?.summary && (
                              <Link
                                to={`/transcripts-summaries/summary?eventId=${summary.event_id}&tab=HIGH&eventName=${summary.title}`}
                              >
                                <HiArrowTopRightOnSquare size={16} />
                              </Link>
                            )}
                          </div>
                          {summary?.summary && <p>{summary.summary}</p>}
                        </li>
                      ))}
                    </ul>
                  )
              ) : (
                <p className='dashboard-placeholder'>No event found!</p>
              )}
            </div>
          </div>
        </li>
      </ul>

      {/* {currentStep === -1 && (
        <>
          <Box mb={12} />
          <Flex position='fixed' zIndex={20} left={0} bottom={4} w='full' justifyContent='center'>
            <Input
              bg='white'
              border='0.5px solid rgba(28, 28, 28, 0.1)'
              boxShadow='0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              _focusVisible={{
                border: '0.5px solid rgba(28, 28, 28, 0.1)',
                boxShadow: '0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              }}
              flex={1}
              maxW='707px'
              color='black'
              transition='none'
              value={newPrompt} 
              placeholder='Try asking “what are the blockers”'
              onChange={e => !setNewPromptLoading || setNewPrompt(e.target.value)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddPrompt(newPrompt)
                }
              }}
            />
          </Flex>
        </>
      )} */}
    </div>
  )
}

export default FunctionalDashboard
